// Mirrors impire-sdk-go/solution/{manifest,panels,wire}.go — the solutions
// family's wire (02-DESIGN/14-solutions/01-the-manifest.md § what the
// manifest declares, § the solutions service and the catalogue;
// 03-the-console.md § the declaration; decision 0038): the manifest and its
// parts, the panel vocabulary, the catalogue's publications, the install
// record, the family's subjects and refusals; and the paths a solution's
// settings and secrets live at. JSON field names are the Go files'; a shape
// that differs is a defect here. A subject is built by the library, never
// inlined.

import { config, solution, subject, vault } from "@impire-io/sdk";

/** SERVICE is the solutions service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "solutions";

/** The family's verbs and the tokens after them. */
export const Verb = {
  publish: "publish",
  withdraw: "withdraw",
  catalogue: "catalogue",
  install: "install",
  installed: "installed",
  upgrade: "upgrade",
  remove: "remove",
} as const;

const LIST = "list";
const GET = "get";

/** The subjects the console speaks, the tokenless form; the import fills the account. */
export const Subjects = {
  catalogueList: () => subject.client(subject.Family.solutions, Verb.catalogue, LIST),
  catalogueGet: (name: string) => subject.client(subject.Family.solutions, Verb.catalogue, GET, name),
  install: (name: string) => subject.client(subject.Family.solutions, Verb.install, name),
  installedList: () => subject.client(subject.Family.solutions, Verb.installed, LIST),
  installedGet: (name: string) => subject.client(subject.Family.solutions, Verb.installed, GET, name),
  upgrade: (name: string) => subject.client(subject.Family.solutions, Verb.upgrade, name),
  remove: (name: string) => subject.client(subject.Family.solutions, Verb.remove, name),
} as const;

/** The family's refusals. */
export const Code = {
  invalid: "invalid",
  taken: "taken",
  notFound: "not-found",
  installed: "installed",
  limit: "limit",
  withdrawn: "withdrawn",
  consentRequired: "consent-required",
  required: "required",
  unauthorized: "unauthorized",
  badRequest: "bad-request",
  failed: "failed",
} as const;

/**
 * The refusals of a licensed manifest's install and upgrade
 * (02-DESIGN/17-plans/03-solution-licenses.md § install and upgrade with a
 * license): none given, one that fails a check — the message names it — and
 * one past its expiry; each message ends with the manifest's hint. The library's.
 */
export const LicenseCode = solution.LicenseCode;

/** Licensing is a manifest's `license` part: the publisher's key and where a license is obtained (the library's). */
export type Licensing = solution.Licensing;

/** InstalledLicense is the license an install record keeps (the library's). */
export type InstalledLicense = solution.InstalledLicense;

/** The three visibilities. */
export const Visibility = {
  workspace: "workspace",
  descendants: "descendants",
  install: "install",
} as const;
export type Visibility = (typeof Visibility)[keyof typeof Visibility];

/** PUBLISHER_PLATFORM is the publisher of the platform's own solutions. */
export const PUBLISHER_PLATFORM = "platform";

/** The install record's states. */
export const RecordState = {
  installing: "installing",
  installed: "installed",
  removed: "removed",
} as const;
export type RecordState = (typeof RecordState)[keyof typeof RecordState];

/** Who performed the install, and so who may remove it. */
export const Installer = {
  admin: "admin",
  steward: "steward",
} as const;
export type Installer = (typeof Installer)[keyof typeof Installer];

/** The two scopes of a setting. */
export const Scope = {
  tenant: "tenant",
  person: "person",
} as const;
export type Scope = (typeof Scope)[keyof typeof Scope];

/** The hierarchy capabilities (impire-sdk-go/relay). */
export type Capability = "audit" | "usage" | "control";

export interface Resources {
  cpus: number;
  memory_mib: number;
}

/** ServedVerb is a verb a workload serves under its own family that the manifest names. */
export interface ServedVerb {
  verb: string;
  admin?: boolean;
  description?: string;
}

/** Instruction is one instruction a controlled workload accepts. */
export interface Instruction {
  name: string;
  body?: string;
  consequence: string;
}

/** Workload is one workload of a solution, by its role. */
export interface Workload {
  role: string;
  description?: string;
  image: string;
  resources: Resources;
  replicas?: number;
  requires?: string[];
  egress?: string[];
  names?: string[];
  publish?: string[];
  subscribe?: string[];
  sends?: Capability[];
  receives?: Capability[];
  serves?: ServedVerb[];
  controller?: Capability;
  instructions?: Instruction[];
  on_grant?: boolean;
}

/** Setting is one field of a solution's settings. */
export interface Setting {
  name: string;
  kind: string;
  label: string;
  hint?: string;
  default?: unknown;
  required?: boolean;
  options?: string[];
  scope: Scope;
  /** The outbound capabilities a grant must send from the workspace for the setting to be asked at all. */
  sends?: Capability[];
  /**
   * The capabilities the workspace must receive from another for the setting
   * to be asked at all: asked only where the install record binds a role on
   * grant that receives one of them. Never beside `sends`.
   */
  receives?: Capability[];
  secret?: boolean;
}

/** Secret is a secret the solution needs from the workspace. */
export interface Secret {
  name: string;
  hint: string;
  required?: boolean;
}

/** Store is one store the install creates as `<solution>-<name>`. */
export interface Store {
  name: string;
  description?: string;
  history?: "full" | "compactable";
  budget?: number;
  types?: Record<string, unknown>;
  indexes?: Record<string, unknown>;
}

export interface RequiredDocument {
  /** `solution`, or a platform image's name. */
  target: string;
  document: Record<string, unknown>;
}

export interface RequiredRule {
  id: string;
  rule: Record<string, unknown>;
}

export interface Required {
  documents?: RequiredDocument[];
  rules?: RequiredRule[];
}

/** TARGET_SOLUTION is the target naming the solution's own required document. */
export const TARGET_SOLUTION = "solution";

/** Page is a solution's own page. */
export interface Page {
  label: string;
  origin: string;
  roles?: string[];
  settings?: boolean;
  person?: boolean;
}

/** The element kinds of the vocabulary. */
export const ElementKind = {
  list: "list",
  record: "record",
  form: "form",
  action: "action",
  chart: "chart",
  status: "status",
  setting: "setting",
} as const;
export type ElementKind = (typeof ElementKind)[keyof typeof ElementKind];

/** The field and column kinds. */
export const FieldKind = {
  text: "text",
  multiline: "multiline",
  number: "number",
  toggle: "toggle",
  choice: "choice",
  duration: "duration",
  secret: "secret",
  time: "time",
  pill: "pill",
  id: "id",
} as const;

export interface Field {
  name: string;
  label: string;
  kind: string;
  hint?: string;
  default?: string;
  required?: boolean;
  options?: string[];
}

export interface StatusState {
  value: string;
  word: string;
  kind: "live" | "warn" | "off";
}

/** Element is one element of a panel; its kind decides which fields it carries. */
export interface Element {
  kind: string;
  label?: string;
  role?: string;
  verb?: string;
  watch?: boolean;
  columns?: Field[];
  key?: string;
  record?: Element;
  filters?: Field[];
  /** Fixed fields a reading element sends in every request's body. */
  params?: Record<string, string>;
  /** The panel's selection field a list's row (its key) or a chart's point (its time) sets. */
  selects?: string;
  /** A record on its own drawn as tiles. */
  tiles?: boolean;
  fields?: Field[];
  success?: string;
  consequence?: string;
  undoable?: boolean;
  row_key?: string;
  actions?: Element[];
  unit?: string;
  chart?: "line" | "bar";
  windows?: ("hour" | "day" | "week" | "month")[];
  states?: StatusState[];
  setting?: string;
}

/** Panel is one declared panel. */
export interface Panel {
  name: string;
  title: string;
  line?: string;
  icon?: string;
  /** Where the panel is listed: for an admin only, or in the person's menu for everyone; absent, by what it calls. */
  audience?: Audience;
  /** The fields the panel may be narrowed by, set by its lists' rows and charts' points. */
  selection?: Field[];
  /** The outbound capabilities a grant must send from the workspace for the panel to exist. */
  sends?: Capability[];
  elements: Element[];
}

/** The panel audiences. */
export const Audience = {
  admin: "admin",
  person: "person",
} as const;
export type Audience = (typeof Audience)[keyof typeof Audience];

/** Manifest is one solution's declaration. */
export interface Manifest {
  name: string;
  display: string;
  description?: string;
  version?: string;
  workloads?: Workload[];
  settings?: Setting[];
  secrets?: Secret[];
  stores?: Store[];
  required?: Required;
  panels?: Panel[];
  page?: Page;
  /** The part that makes the solution licensed: absent on a free one. */
  license?: Licensing;
}

/** Publication is one manifest in the catalogue at one revision. */
export interface Publication {
  name: string;
  revision: number;
  publisher: string;
  visibility: Visibility;
  /** RFC 3339. */
  published: string;
  by?: string;
  /** The reference pinned to its digest, per role. */
  images?: Record<string, string>;
  manifest: Manifest;
}

/** Entry is a name in the catalogue, with whether its latest revision is licensed and the manifest's hint. */
export interface Entry extends solution.EntryLicensing {
  latest: Publication;
  withdrawn?: boolean;
}

export interface CatalogueListReply {
  solutions: Entry[];
}

/** RevisionRequest names a revision; absent is the latest. */
export interface RevisionRequest {
  revision?: number;
}

/**
 * InstallRequest is the body of an install and an upgrade: the revision
 * consented to and, for a licensed manifest, the license as it was issued
 * (the library's).
 */
export type InstallRequest = solution.InstallRequest;

/** Consent is who consented to which revision, and when. */
export interface Consent {
  revision: number;
  installer: Installer;
  principal?: string;
  display?: string;
  /** RFC 3339. */
  at: string;
}

/** Record is a workspace's install record, with the license accepted for a licensed manifest. */
export interface Record_ extends solution.RecordLicensing {
  name: string;
  tenant: string;
  revision: number;
  publisher: string;
  installer: Installer;
  workloads?: Record<string, string>;
  consents: Consent[];
  state: RecordState;
  latest?: number;
  /** RFC 3339. */
  installed: string;
  updated: string;
  removed?: string;
  manifest?: Manifest;
  /** What the latest revision widens, in words; empty when it only narrows. */
  widens?: string[];
  withdrawn?: boolean;
  visibility?: Visibility;
}

export interface InstalledListReply {
  solutions: Record_[];
}

/**
 * SolutionRef is what a solution registration carries on the workload
 * service's record (impire-workloads/wire, the solution registration).
 */
export interface SolutionRef {
  name: string;
  revision: number;
  role: string;
  installer?: { kind: string; subject?: string; principal?: string; display?: string };
}

// --- where a solution's things live (02-authority.md § where a solution's things live) ---

/** storeName is the name the install creates a declared store under: `<solution>-<store>`. */
export function storeName(solution: string, store: string): string {
  return `${solution}-${store}`;
}

/** secretPath is a tenant-scoped secret's path in the workspace's vault: `solution.<name>.<secret>`. */
export function secretPath(solution: string, secret: string): string {
  return vault.solutionPath(solution, secret);
}

/** secretPrefix is the solution's own prefix, which an admin lists: `solution.<name>`. */
export function secretPrefix(solution: string): string {
  return vault.solutionPath(solution);
}

const PERSON_SOLUTION = "solution";

/** personSettingPath is a person-scoped setting's path: `person.<principal>.solution.<name>.<setting>`. */
export function personSettingPath(principal: string, solution: string, setting: string): string {
  return vault.personPath(principal, PERSON_SOLUTION, solution, setting);
}

/** personPrefix is the person's own paths for every solution: `person.<principal>.solution`. */
export function personPrefix(principal: string): string {
  return vault.personPath(principal, PERSON_SOLUTION);
}

/** personSettingOf reads `person.<principal>.solution.<name>.<setting>` back. */
export function personSettingOf(path: string): { solution: string; setting: string } | undefined {
  const toks = subject.tokens(path);
  const [p, , seg, solution, setting] = toks;
  if (
    toks.length !== 5 ||
    p !== vault.PERSON_PREFIX ||
    seg !== PERSON_SOLUTION ||
    solution === undefined ||
    setting === undefined
  ) {
    return undefined;
  }
  return { solution, setting };
}

/** The documents of the solution kind of the configuration service. */
export const DOCUMENT_SETTINGS = config.DOCUMENT_SETTINGS;
export const DOCUMENT_REQUIRED = config.DOCUMENT_REQUIRED;

/**
 * verbSubject is a verb of one of the solution's workloads,
 * `workload.<id>.<verb>`, and for a record or a row's action the key the row
 * supplies as the token after it, `workload.<id>.<verb>.<key>`.
 */
export function verbSubject(id: string, verb: string, key?: string): string {
  if (
    !subject.validToken(id) ||
    !subject.validToken(verb) ||
    (key !== undefined && !subject.validToken(key))
  ) {
    throw new Error(`solutions: ${[id, verb, key ?? ""].join(" ")} is not a verb of a workload`);
  }
  return key === undefined
    ? subject.client(subject.Family.workload, id, verb)
    : subject.client(subject.Family.workload, id, verb, key);
}

/**
 * serviceOf is the service name a solution workload's verbs are sealed
 * toward: the first name its role declares — what its placements answer
 * discovery under, vouched by the platform — and the workload's id where it
 * declares none.
 */
export function serviceOf(m: Manifest, role: string, id: string): string {
  return m.workloads?.find((w) => w.role === role)?.names?.[0] ?? id;
}

// --- the frame (03-the-console.md § the frame's credential, § the handshake) ---

/**
 * FrameReply is the frame credential `identity.frames.issue.<solution>`
 * answers once: the opaque credential, its expiry, and the frame app's id
 * where the service names it, which the console revokes on the way out.
 * Kept here alone, so that the auth service's shape is adjusted in one place.
 */
export interface FrameReply {
  credential: string;
  /** RFC 3339. */
  expires: string;
  app?: string;
}

/** The messages of the handshake, by their `type`. */
export const FrameMessage = {
  ready: "ready",
  session: "session",
  theme: "theme",
  credential: "credential",
  badge: "badge",
  navigate: "navigate",
  height: "height",
} as const;

/** SessionMessage is what the console sends after `ready`. */
export interface SessionMessage {
  type: typeof FrameMessage.session;
  credential: string;
  expires: string;
  websocket_url: string;
  sentinel: string;
  workspace: { slug: string; name: string };
  person: { display: string; role: string };
  workloads: Record<string, string>;
}

export interface ThemeMessage {
  type: typeof FrameMessage.theme;
  mode: "light" | "dark";
  tokens: Record<string, string>;
}

export interface CredentialMessage {
  type: typeof FrameMessage.credential;
  credential: string;
  expires: string;
}

/** FromFrame is a message a frame may send; anything else is ignored. */
export type FromFrame =
  | { type: typeof FrameMessage.ready }
  | { type: typeof FrameMessage.badge; count?: number; state?: "live" | "warn" | "off" }
  | { type: typeof FrameMessage.navigate; address: string }
  | { type: typeof FrameMessage.height; height: number };

/**
 * readFrameMessage validates a message from the frame: one of the four
 * kinds with its fields of the right shape, or undefined.
 */
export function readFrameMessage(data: unknown): FromFrame | undefined {
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  const m = data as Record<string, unknown>;
  switch (m.type) {
    case FrameMessage.ready:
      return { type: FrameMessage.ready };
    case FrameMessage.badge: {
      const count = m.count;
      const state = m.state;
      if (typeof count === "number" && Number.isInteger(count) && count >= 0) {
        return { type: FrameMessage.badge, count: Math.min(count, 999) };
      }
      if (state === "live" || state === "warn" || state === "off") {
        return { type: FrameMessage.badge, state };
      }
      return undefined;
    }
    case FrameMessage.navigate:
      return typeof m.address === "string" ? { type: FrameMessage.navigate, address: m.address } : undefined;
    case FrameMessage.height:
      return typeof m.height === "number" && Number.isFinite(m.height) && m.height >= 0
        ? { type: FrameMessage.height, height: Math.min(m.height, 100000) }
        : undefined;
  }
  return undefined;
}

/**
 * pageOrigin is the declared origin as the browser compares it, or undefined
 * when it is not one the console frames: https, or http on the loopback alone
 * — the development stand-up serving a platform solution's page beside the
 * console (19-chat/01 § the page's origin) — the rule the manifest's
 * validation applies (impire-sdk-go solution.PageScheme).
 */
export function pageOrigin(p: Page): string | undefined {
  try {
    const u = new URL(p.origin);
    if (u.host === "" || !pageScheme(u)) {
      return undefined;
    }
    return u.origin;
  } catch {
    return undefined;
  }
}

function pageScheme(u: URL): boolean {
  if (u.protocol === "https:") {
    return true;
  }
  return u.protocol === "http:" && ["127.0.0.1", "localhost", "[::1]"].includes(u.hostname);
}

// --- the declaration's words (01-the-manifest.md § the manifest as the consent surface) ---

/** address is a subject the builder made, without the root: the form a manifest's patterns are written in. */
function address(subj: string): string {
  return subject.address(subj).join(subject.SEP);
}

/** OBSERVE is the observation event's family (02-DESIGN/11-observe/02-the-observation-event.md). */
const OBSERVE = "observe";

/** PATTERN_WORDS are the words of the patterns the design names. */
export const PATTERN_WORDS = {
  suspend: "stop any workload in this workspace",
  resume: "start any workload in this workspace again",
} as const;

/** KNOWN maps the declared patterns the design gives words for. */
const KNOWN: ReadonlyMap<string, string> = new Map([
  [address(subject.client(subject.Family.workloads, "suspend", "*")), PATTERN_WORDS.suspend],
  [address(subject.client(subject.Family.workloads, "resume", "*")), PATTERN_WORDS.resume],
  [address(subject.client(subject.Family.workloads, "list")), "list every workload in this workspace"],
  [
    address(subject.client(subject.Family.workloads, "get", "*")),
    "read any workload's registration in this workspace",
  ],
  // The observation event's family, which the library does not build yet (impire-sdk-go subject.Observe).
  [subject.join(OBSERVE, ">"), "see every call made in this workspace, never its content"],
  [address(subject.client(subject.Family.workload, "*", subject.TOKEN_INFER, ">")), "any model"],
  [address(subject.client(subject.Family.workload, "*", subject.TOKEN_TOOL, ">")), "any toolkit's tools"],
]);

/** patternWords is what one declared pattern lets the workload do, or undefined where the platform's words are all there is. */
export function patternWords(pattern: string): string | undefined {
  const known = KNOWN.get(pattern);
  if (known !== undefined) {
    return known;
  }
  const toks = subject.tokens(pattern);
  const last = toks[toks.length - 1];
  if (toks[0] === OBSERVE && toks.length >= 3 && last === ">") {
    return `see every call to ${toks.slice(1, -1).join(" ")} in this workspace, never its content`;
  }
  if (toks[0] === subject.Family.audit) {
    return "see who signed in to this workspace";
  }
  if (toks[0] === subject.Family.placements) {
    return "read what this workspace's workloads wrote";
  }
  return undefined;
}
